/**
  * @class msysUser
  * @description
  * msysUser is a special service for encapsulating some common functions
  * related to users and applications in client Mosy applications.
  * Main features include:
  * - managing login process of user in running application
  * - managing process of changing user’s password
  * - managing reporting of user’s operational status to mServer
  * - performs automatic re-login after reconnection to server
  * - creates translations for security zone names visible for current user
  * - creates translations for application info visible for current user
  * - creates translations for information about other users visible for current user
  *
  * IMPORTANT: empty mmUserLogged actually means log out
  */

import msysListener from 'msysListener'
const mcrypt = require('msysCrypt')

export default class msysUser extends msysListener {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysLogger'], dependencies)
    cfg_schema = _.extend({
      auto_login: { description: 'automatic user login', type: 'string' },
      auto_password: { description: 'automatic user password', type: 'string' },
      auto_callsign: { description: 'automatic user callsign', type: 'string' },
      auto_userid: { description: 'simulates successful logging in from server', type: 'integer' },
      auto_privatekey: { description: "simulates user's private key from server", type: 'string' },
      login_timeout: {
        description: 'timeout in ms for return of login response',
        type: 'number',
        default: 10000,
      },
      offline_status: {
        description: 'user status set when offline',
        type: 'string',
        default: 'bout',
      },
      initial_status: {
        description: 'user status set automatically on successful log in; see mmUserStatus',
        type: 'string',
        default: 'gready',
      },
      delay_watch: { type: 'integer', description: 'delay of sending mmWatch after user logged', default: 1000 },
      login_server: {
        type: 'string',
        description: 'listener for handling login requests',
        default: ':msvrUsers',
      },
      users_server: {
        type: 'string',
        description: 'listener for watching users',
        default: ':msvrUsers',
      },
      zones_server: {
        type: 'string',
        description: 'listener for watching security zones',
        default: ':msvrZones',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)

    this.login_sender = {
      scope: this.config.login_server.replace(/\w+/g, ''),
      listener_id: this.config.login_server.replace(':', ''),
    }

    this.status = this.config.initial_status
    this.applications = {}
    this.users = {}
    this.zones = {} // new security zones
    this.groups = {} // old security groups
    this.applications_version = 0
    this.users_version = 0
    this.groups_version = 0
    this.zones_version = 0
    LANG.createOptions('user.login.', { '': LANG.ZERO_SPACE }, true)
    LANG.createOptions('user.fullname.', { '': LANG.ZERO_SPACE }, true)
    LANG.createOptions('user.online.', { '': LANG.ZERO_SPACE }, true)
    LANG.createOptions('user.icon.', { '': '' }, true)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmStart(sender, params) {
    // try to obtain username/password from other browser window already running
    super.mmStart(sender, params)
    this.subscribe('#APPS')
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Automatically invoked when all expected listeners were started or exited
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.init] - timestamp when application starts with loading listeners
  * @param {integer} [params.loaded] - timestamp when all listeners are loaded and first listener starts
  * @param {integer} [params.started] - timestamp when all listeners started
  */
  mmStartedAll(sender, params) {
    this.publish('#ALL', 'mmUserStatus', { status: this.status })
    this.publish('#ALL', 'mmUsers', { full: true, version: this.users_version, list: Object.values(this.users) })

    if(this.config.auto_userid>0) {
      this.mmUserLogged(
        this.login_sender,
        {
          login: this.config.auto_login,
          password: this.config.auto_password,
          callsign: this.config.auto_callsign,
          user_id: this.config.auto_userid,
          privatekey: this.config.auto_privatekey,
        },
      )

      setTimeout(()=>{
        this.publish('#ALL', 'mmModel', { md5: 'offline' })
      }, 100)
    }
  } // mmStartedAll

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * request to msysUser resp. msvcUser to de/authenticate the user and optionally set callsign
  * >msysUser.mmUserLogin?{} - will not change authorization, just verify it with server and return information
  * >msysUser.mmUserLogin?{login:''} - will log out user and unassign callsign
  * >msysUser.mmUserLogin?{login:'...',password:'...'} - will log in user and unassign callsign
  * >msysUser.mmUserLogin?{login:'...',password:'...',callsign:'...} - will log in user and assign callsign (if possible)
  * >msysUser.mmUserLogin?{callsign:''} - will unassign callsign but keep current user logged in; sign out will be on all user's devices if configured on server's callsign_duplicate
  * >msysUser.mmUserLogin?{callsign:'..'} - will try to assign callsign but keep current login; assign will be on all user's devices if configured on server's callsign_duplicate
  *
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.login] - user's login name
  * @param {string} [params.password] - user's password
  * @param {string} [params.callsign] - user's callsign
  */
  async mmUserLogin(sender, params) {
    if(this.config.login_server) {
      try{
        const msg = await this.request(this.config.login_server, 'mmUserLogin', params, { resolve: 'mmUserLogged', timeout: this.config.login_timeout })
        this.mmUserLogged(msg.sender, { ...msg.params, password: params.password })
      }catch(e) {
        this.error('Login error: '+e)
        this._userLogout()
      }
    }
  } // mmUserLogin

  /**
   * @private
   */
  _userLogout() {
    // empty mmUserLogged actually means log out
    this.publishAs(this.login_sender, 'msysUser', 'mmUserLogged', {})
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    const was_user_id = this.user_id
    let output_params = {}
    let new_status = this.status

    // store user roles as string in <body> for CSS checks
    if(typeof document !== 'undefined' && document?.body) {
      let roles = params.roles || {}
      if(typeof roles !== 'object') roles = {}
      let roles_str = Object.entries(roles).map(([role, zone])=>`,${role}:${zone}`).join('')
      if(roles_str.length>0) roles_str = roles_str + ','
      document.body.setAttribute('user-roles', roles_str)
    }

    if(sender.scope==='~' && sender.listener_id==='msysUser') {
      this.user_id = params.user_id
      this.login = _.toString(params.login)
      this.callsign = _.toString(params.callsign)
      this.private_key = mcrypt.privateFromPEM(params.privatekey, sender.instance_id)
      if(this.user_id && this.private_key) {
        output_params = _.omit(params, ['privatekey', 'password'])
      }else{
        this.user_id = this.login = this.callsign = this.private_key = ''
        new_status = this.config.offline_status
        if(params.error) output_params = { error: params.error }
      }
    }else if(sender.scope===this.login_sender.scope && sender.listener_id===this.login_sender.listener_id) {
      const user_id = params.user_id
      if(params.password) {
        // this is reply to our login request which included password
        const password = _.toString(params.password)
        this.private_key = mcrypt.privateFromPEM(params.privatekey, password)
        if(this.private_key) {
          this.user_id = user_id
          this.login = _.toString(params.login)
          this.callsign = _.toString(params.callsign)
          output_params = _.omit(params, ['privatekey', 'password'])
          new_status = this.config.initial_status
        }else{
          // we cannot decrypt private key, or login failed with error
          // empty all credentials and set status to OUT
          this.user_id = this.login = this.callsign = this.private_key = ''
          new_status = this.config.offline_status
          if(params.error) output_params = { error: params.error }
        }
      }else{
        // server informed us on login change without login request with password
        // we can continue only if user_id has not changed, as otherwise we don't know the password for decrypting private key
        if(this.user_id !== params.user_id || !this.private_key) {
          // if user changed, empty all credentials and set status to OUT
          this.user_id = this.login = this.callsign = this.private_key = ''
          new_status = this.config.offline_status
          if(params.error) output_params = { error: params.error }
        }else{
          // our user_id is still the same as server thinks, so we can keep all as it is
          this.callsign = _.toString(params.callsign)
          output_params = _.omit(params, ['privatekey', 'password'])
        }
      }

      const out = {
        user_id: this.user_id,
        login: this.login,
        callsign: this.callsign,
        privatekey: this.private_key ? mcrypt.privateToPEM(this.private_key, this.instance_id) : '',
      }
      if(out.user_id==='') delete out.user_id
      this.status = new_status
      this.publish('~msysUser', 'mmUserLogged', out)
    }else{
      return this.error('Invalid user server')
    }

    // translate current user data
    LANG.createTranslation('logged.user_id', params.user_id??'')
    LANG.createTranslation('logged.login', params.login??'')
    LANG.createTranslation('logged.callsign', params.callsign??'')
    const current = this.users[this.user_id]
    LANG.createTranslation('logged.fullname', current ? current.firstname+' '+current.lastname : '')
    LANG.createTranslation('logged.icon', current?.icon ?? '')

    this.publish('#ALL', 'mmUserLogged', output_params)
    this.publish('#ALL', 'mmUserStatus', { status: new_status })
    this.publish('SERVER@msysPeeringServer', 'mmUserLogged', output_params) // notify peering server on client's name change
    this.publish('SERVER@msysPeeringServer', 'mmUserStatus', { status: new_status })
    if(this.config.login_server) {
      this.publish(this.config.login_server, 'mmUserStatus', { status: new_status })
    }

    // ask to watch for users/groups changes
    // if user_id changed, ask for full update, otherwise use last received version as a starting point
    if(was_user_id !== this.user_id) {
      this.users_version = 0
      this.groups_version = 0
      this.groups = {}
    }

    // new workflow-based server
    setTimeout(()=>{
      if(this.config.users_server) {
        this.publish(this.config.users_server, 'mmWatch', { version: this.users_version })
      }
      if(MOSY_APP==='mOffice' && this.config.zones_server) {
        this.publish(this.config.zones_server, 'mmWatch', { version: this.zones_version })
      }
    }, this.config.delay_watch)

    // direct reply to sender if needed
    if(sender.request_id) {
      this.reply(sender, 'mmUserLogged', output_params)
    }

    this._refreshCurrentUser()
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * request to msysUser/msvrUsers/3rd party to change user's password
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.password - user's current password
  * @param {string} params.newpassword - user's new password
  */
  async mmUserPasswordChange(sender, params) {
    if(this.config.login_server) {
      try{
        const { params: rparams } = await this.request(this.config.login_server, 'mmAction', {
          id: this.user_id,
          action: 'CHANGEPWD',
          params,
        }, { resolve: 'mmObjects', timeout: this.config.login_timeout })
        this.private_key = mcrypt.privateFromPEM(rparams.list[0].privatekey, params.newpassword)
        if(sender && sender.request_id) this.reply(sender, 'mmSuccess')
      }catch(e) {
        if(sender && sender.request_id) this.reply(sender, 'mmFailure', { error: String(e) })
      }
    }else {
      this.reply(sender, 'mmFailure', { error: 'Login server not configured' })
    }
  } // mmUserPasswordChange

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * notification on changed user status
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} params.status - new status
  * @param {string} [params.info] - additional info about status change
  */
  mmUserStatus(sender, params) {
    // when msysUser receives change of status, it will publish it to all listeners and to server as well
    if(this.status !== params.status) {
      this.status = params.status
      this.publish('#ALL', 'mmUserStatus', params)
      this.publish('SERVER@msysPeeringServer', 'mmUserStatus', params)
      if(this.config.login_server) {
        this.publish(this.config.login_server, 'mmUserStatus', params)
      }
    }
  } // mmUserStatus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    // try to re-login providing just current login and callsign, no password needed
    // server then verifies if it still remembers the same login for our app_id and if so,
    // will send back mmUserLogged with user_id and callsign

    const disable_autologin = typeof nw !== 'undefined' && nw?.global?.disable_autologin
    if(this.config.auto_login && this.config.auto_password && !disable_autologin) {
      this.mmUserLogin(null, {
        login: this.config.auto_login,
        password: this.config.auto_password,
        callsign: this.config.auto_callsign,
      })
    }else{
      this.mmUserLogin(null, {
        login: this.login,
        password: '',
        callsign: this.callsign,
      })
    }

    // publish our current status
    if(this.config.login_server) {
      this.publish(this.config.login_server, 'mmUserStatus', { status: this.status })
      setTimeout(()=>{
        // repeat after some time after reconnect
        this.publish(this.config.login_server, 'mmUserStatus', { status: this.status })
      }, 2000)
    }
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server sent list of applications
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.list] - array of changed records
  */
  mmApplications(sender, params) {
    if(this.applications_version >= params.version) return
    this.applications_version = params.version

    if(params.full) this.applications = {}
    params.list.forEach(change=>{ this.applications[change.id] = change })

    const mapping = {
      type: 'apptype',
      host: 'host',
      device: 'device_id',
      vehicle: 'vehicle',
      owner: 'owner_id',
      location: 'location',
      note: 'note',
    }

    let any_changed = false
    for(const key in mapping) {
      const field = mapping[key]

      // clear old options on full refresh
      if(params.full) {
        any_changed = true
        LANG.createOptions(`app.${key}.`, { '': ' ' }, true)
      }

      // append translations
      let changed = false
      params.list.forEach(app=>{
        const value = _.toString(app[field])
        if(this._changeTranslationOption(`app.${key}.${app.id}`, value)) {
          changed = true
        }
      })

      // refresh elements depending on this translation
      if(changed) {
        any_changed = true
        this._retranslateElements(`app.${key}.`)
      }
    }

    if(any_changed) {
      document.body.forAll('.mele-table.app-dependent', el=>el.retranslate())
    }

    params.list.forEach(app=>{
      const online = app.online ? 'sys.online.1.0' : 'sys.online.0.0'
      if(app.user_id && this._changeTranslationOption(`user.online.${app.user_id}`, online)) {
        try {
          // eslint-disable-next-line no-template-curly-in-string
          document.body.forAll('.mele-list[auto][iconright*="user.online"]', el=>el.setProperty('iconright', 'user.online.${key}', true, true))
        } catch(e) {}
      }
    })
  } // mmApplications

  _changeTranslationOption(key, value) {
    const existing_translations = LANG.dictionary[key]
    const old_value = _.toString(existing_translations ? existing_translations[0] : '') // we expect all translations are the same, so we can use first for check
    if(value === old_value) return false // no change
    if(value==='') {
      delete LANG.dictionary[key]
    }else{
      LANG.dictionary[key] = LANG.languages.map(()=>value)
    }
    return true
  } // _changeTranslationOption

  _retranslateElements(prefix) {
    if(!document || !document.body || !document.body.forAll) return
    const selector = `[prefix="${prefix}"]`
    document.body.forAll(`.mele-list[auto]${selector}, .mele-input${selector}, mele-button${selector}`, el=>el.retranslate())
  } // _retranslateElements

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    this.wf = true // to know we are working with new workflow msvr
    if(params.type==='users') {
      params.list.forEach(item=>{
        item.enabled = item.state==='ENABLED'
      })
      this.mmUsers(sender, _.omit(params, ['type']))
    }else if(params.type==='zones') {
      this.setupZones(params)
    }
  } // mmObjects

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server notifies watchers that some system users were updated
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - whether all fields or just subset is sent
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} params.version - version of data sent by server
  * @param {array} params.list - array of changed records
  */
  mmUsers(sender, params) {
    if(params.version >= this.users_version) {
      this.users_version = params.version

      if(params.full) this.users = {}
      params.list.forEach(change=>{ this.users[change.id] = change })

      if(params.full) {
        LANG.createOptions('user.login.', { '': LANG.ZERO_SPACE }, true)
        LANG.createOptions('user.fullname.', { '': LANG.ZERO_SPACE }, true)
        LANG.createOptions('user.icon.', { '': '' }, true)
      }

      params.list.forEach(user=>{
        LANG.createOptions('user.login.', { [user.id]: user.login })
        LANG.createOptions('user.fullname.', { [user.id]: user.firstname+' '+user.lastname })
        LANG.createOptions('user.icon.', { [user.id]: user.icon })
      })

      // translate current user data
      const current = this.users[this.user_id]
      LANG.createTranslation('logged.fullname', current ? current.firstname+' '+current.lastname : '')
      LANG.createTranslation('logged.icon', current?.icon ?? '')

      try{
        document.body.forAll('.mele-list[auto][prefix="user.login."]', el=>el.autoChanged(true))
        document.body.forAll('.mele-list[auto][prefix="user.fullname."]', el=>el.autoChanged(true))
        document.body.forAll('.mele-input[type=string][prefix="user.login."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('.mele-input[type=string][prefix="user.fullname."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('mele-button[prefix="user.login."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('mele-button[prefix="user.fullname."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('.mele-table.user-dependent', el=>el.rowattributesChanged())
      }catch(e) {}

      this._refreshCurrentUser()

      this.publish('#USERS', 'mmUsers', params) // forward message to #USERS queue
    }
  } // mmUsers

  _refreshCurrentUser() {
    const id = _.natural(this.user_id) || ''
    const user = this.users[id]
    LANG.createOptions('', {
      'current_user.id': id,
      'current_user.login': (user ? _.toString(user.login) : '') || ' ',
      'current_user.fullname': (user ? _.toString(user.firstname)+' '+_.toString(user.lastname) : '') || ' ',
    })
    try{
      document.body.forAll('.mele-button[label="current_user.login"]', el=>el.labelChanged(el.label, el.label))
      document.body.forAll('.mele-button[label="current_user.fullname"]', el=>el.labelChanged(el.label, el.label))
    }catch(e) {}

    // !!!!!IMPORTANT: this is a hack for BG project, which is non-workflow.
    if(MOSY_CUSTOMER !== 'BG') {
      const roles = _.toString(_.get(user, 'roles')).split(',').map(role=>{
        const m = /^(\w+):(\d+)$/.exec(role)
        return m ? m[1] : null
      }).filter(role=>!!role).sort()
      this.publish('#USERS', 'mmUserRoles', { roles })
    }
  } // _refreshCurrentUser

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * update list of security zones
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {number} [params.version] - version of data sent by server
  * @param {array} [params.list] - array of changed records
  */
  setupZones(params) {
    if(params.version >= this.zones_version) {
      this.zones_version = params.version

      if(params.full) this.zones = {}
      params.list.forEach(change=>{ this.zones[change.id] = change })

      if(params.full) {
        LANG.createOptions('zone.name.', { '': LANG.ZERO_SPACE }, true)
      }

      params.list.forEach(zone=>{
        LANG.createOptions('zone.name.', { [zone.id]: zone.name })
      })

      try{
        document.body.forAll('.mele-list[auto][prefix="zone.name."]', el=>el.autoChanged(true))
        document.body.forAll('.mele-input[type=string][prefix="zone.name."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('mele-button[prefix="zone.name."]', el=>el.prefixChanged(el.prefix, el.prefix))
        document.body.forAll('.mele-table.zone-dependent', el=>el.rowattributesChanged())
      }catch(e) {}
    }
  } // setupZones

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server notifies clients that some system user groups were updated
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {number} params.version - version of data sent by server
  * @param {array} [params.list] - array of changed records
  */
  publishUserRoles() {
    const user_id = this.user_id
    let my_roles = {}
    for(const group_id in this.groups) {
      const roles = this.groups[group_id].roles || {}
      for(const role in roles) {
        const members = roles[role]
        if(members.indexOf(user_id)>=0) { my_roles[role]=1 }
      }
    }

    my_roles = _.keys(my_roles).sort()
    this.publish('#USERS', 'mmUserRoles', { roles: my_roles })
  } // publishUserRoles

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.url] - URL of connected peering
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the peering
  */
  mmPeeringConnected(sender, params) {
    // notify peering server on client's logged user/callsign
    let user_info = {
      user_id: this.user_id,
      login: this.login,
      callsign: this.callsign,
    }
    if(!this.user_id) user_info = {}
    this.publish('SERVER@msysPeeringServer', 'mmUserLogged', user_info)
    this.publish('SERVER@msysPeeringServer', 'mmUserStatus', { status: this.status })
  } // mmPeeringConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information that another browser tab has been connected
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmTabConnected(sender, params) {
    if(this.user_id && this.private_key) {
      sender.listener_id = 'msysUser'
      this.publish(sender, 'mmUserLogged', {
        user_id: this.user_id,
        login: this.login,
        callsign: this.callsign,
        privatekey: mcrypt.privateToPEM(this.private_key, this.instance_id),
      })
      this.publish(sender, 'mmUserStatus', { status: this.status })
    }
  } // mmTabConnected
} // msysUser
